import { zodResolver } from "@hookform/resolvers/zod";
import { KikMarketingCloudDoubleOptInProcedures } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import {
  kikEmailUpdateSuperRefiner,
  KikEmailUpdateWithActionCodeFields,
  kikEmailUpdateWithActionCodeFieldsSchema,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import router from "next/router";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import IconEdit from "@/icons/icon-edit";
import Button from "~/components/common/button";
import richTextStyle from "~/components/common/rich-text.module.scss";
import Errors from "~/components/form/errors";
import InputField from "~/components/form/input-field";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useFormTranslations } from "~/hooks/use-form-translations";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import useTimer from "~/hooks/use-timer";
import Logger from "~/services/logger/logger";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";

import Dialog from "../../common/dialog";
import Icon from "../../common/icon";
import styles from "./settings.module.scss";
import updateEmailDialogStyles from "./update-email.module.scss";

type Props = {
  isOpen?: boolean;
  onClose: () => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
};

export default function UpdateEmail({ isOpen = !1, onAfterOpen, onAfterClose, onClose }: Props) {
  const { authError, isLoading, sendEmailVerification, emailUpdateCompletion, user } = useAuthContext();
  const { newSuccessGlobalNotification } = useGlobalNotification();
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);
  const currentLanguage = crossCountryUtils.getCurrentLanguageCode(router);
  const { getFieldError } = useFormTranslations();
  const { timeLeft, resetTimer } = useTimer();
  const [step, setStep] = useState<0 | 1>(0);
  const submitTexts = [t("generic.submit"), t("generic.myaccount.settings.updateEmailCta")];

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<KikEmailUpdateWithActionCodeFields>({
    ...Constants.USE_FORM_VALIDATION_DEFAULTS,
    resolver: [
      zodResolver(
        kikEmailUpdateSuperRefiner(
          kikEmailUpdateWithActionCodeFieldsSchema.omit({
            actionCode: !0,
          })
        )
      ),
      zodResolver(kikEmailUpdateWithActionCodeFieldsSchema),
    ][step],
  });

  const doEmailUpdate = (data: KikEmailUpdateWithActionCodeFields) => {
    return emailUpdateCompletion(data).then(() => {
      reset();
      newSuccessGlobalNotification(
        {
          slug: "updateEmailConfirmation",
          closeOnClickCta: true,
          ctaCaption: "generic.ok",
        },
        {
          timeout: 5000,
        }
      );
    });
  };

  const doSendEmailVerification = (data: Pick<KikEmailUpdateWithActionCodeFields, "EmailAddress"> = getValues()) => {
    return sendEmailVerification({
      FirstName: user!.FirstName!,
      EmailAddress: data.EmailAddress,
      procedure: KikMarketingCloudDoubleOptInProcedures.UPDATE_EMAIL,
      CountryID: currentCountry,
      LanguageID: currentLanguage,
    })
      .then(() => {
        setStep(1);
        resetTimer();
      })
      .catch((err) => Logger.instance.error("UpdateEmail errored > ", err));
  };

  const handleAfterOpen = () => {
    setStep(0);
    onAfterOpen?.();
  };

  const onSubmit: SubmitHandler<KikEmailUpdateWithActionCodeFields> = (data) => {
    return [doSendEmailVerification, doEmailUpdate][step](data);
  };

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <Icon icon={IconEdit} />
          <span>{t("generic.myaccount.settings.change_email")}</span>
        </>
      }
      onClose={onClose}
      onAfterOpen={() => handleAfterOpen()}
      onAfterClose={onAfterClose}
      customStyles={updateEmailDialogStyles}
    >
      <p className={styles.editSettingsFormDescription}>{t("generic.myaccount.settings.changeEmailSubtitle")}</p>
      <form
        onSubmit={handleSubmit(onSubmit, (data) => Logger.instance.warn("Update email form invalid", data))}
        className={styles.editSettingsForm}
        noValidate={true}
      >
        <input {...register("OldEmailAddress")} type="hidden" value={user?.EmailAddress} />
        {/* I had to duplicate the form cause the readonly state doesn't get updated in the InputField */}
        {step < 1 && (
          <>
            <InputField
              {...register("EmailAddress")}
              autoComplete="off"
              type="email"
              label={t("generic.newEmail")}
              error={
                errors.EmailAddress?.message
                  ? t(errors.EmailAddress?.message, {
                      fieldName: t("generic.newEmail"),
                      maxLength: KikUserZodSchemas.emailMaxlength,
                    })
                  : ""
              }
              onClearErrors={() => clearErrors("EmailAddress")}
            />
            <InputField
              {...register("ConfirmEmailAddress")}
              autoComplete="off"
              type="email"
              label={t("generic.repeatNewEmail")}
              error={getFieldError(errors.ConfirmEmailAddress?.message, {
                fieldName: t("generic.repeatNewEmail"),
                maxLength: KikUserZodSchemas.emailMaxlength,
              })}
              onClearErrors={() => clearErrors("ConfirmEmailAddress")}
            />
          </>
        )}
        {step > 0 && (
          <>
            <InputField
              {...register("EmailAddress")}
              autoComplete="off"
              type="email"
              label={t("generic.newEmail")}
              readOnly={step > 0}
              error={
                errors.EmailAddress?.message
                  ? t(errors.EmailAddress?.message, {
                      fieldName: t("generic.newEmail"),
                      maxLength: KikUserZodSchemas.emailMaxlength,
                    })
                  : ""
              }
              onClearErrors={() => clearErrors("EmailAddress")}
            />
            <InputField
              {...register("ConfirmEmailAddress")}
              autoComplete="off"
              type="email"
              label={t("generic.repeatNewEmail")}
              readOnly={step > 0}
              error={getFieldError(errors.ConfirmEmailAddress?.message, {
                fieldName: t("generic.repeatNewEmail"),
                maxLength: KikUserZodSchemas.emailMaxlength,
              })}
              onClearErrors={() => clearErrors("ConfirmEmailAddress")}
            />
            <p className={styles.doubleOptInDescription}>
              {t.rich("generic.signUpDoubleOptInDesc", {
                br: () => <br />,
                email: () => <strong>{getValues("EmailAddress")}</strong>,
              })}
            </p>
            <InputField
              type="text"
              label={t("generic.form.action_code")}
              error={getFieldError(errors.actionCode?.message, {
                fieldName: t("generic.form.action_code"),
                minLength: KikUserZodSchemas.actionCodeLength,
              })}
              {...register("actionCode")}
              isSubmitting={isSubmitting}
              maxLength={KikUserZodSchemas.actionCodeLength}
              onClearErrors={() => clearErrors("actionCode")}
              className={styles.input}
            />
          </>
        )}
        {timeLeft && (
          <p className={richTextStyle.paragraph}>
            {t.rich("generic.signUpDoubleOptInSendAnotherIn", {
              seconds: timeLeft,
            })}
          </p>
        )}
        {step > 0 && (
          <p className={richTextStyle.paragraph}>
            {t.rich("generic.signUpDoubleOptInActionCodeNotReceived", {
              sendAnother: (chunks) => (
                <Button
                  className={richTextStyle.link}
                  lookAsLink
                  disabled={isLoading || !!timeLeft}
                  onClick={() => doSendEmailVerification()}
                >
                  {chunks}
                </Button>
              ),
            })}
          </p>
        )}
        <Errors error={authError} style={{ gridColumn: breakpoint === "desktop" ? "2 span" : "1" }} />

        <Button
          className={styles.submitModal}
          variant="secondary"
          type="submit"
          disabled={isSubmitting || isLoading || (step < 1 && !!timeLeft)}
          loading={isSubmitting || isLoading}
        >
          {submitTexts[step]}
        </Button>
        {step < 1 && !!timeLeft && (
          <Button
            className={styles.submitModal}
            variant="secondary"
            onClick={() => setStep(1)}
            type="submit"
            disabled={step > 0 || !timeLeft}
          >
            {t("generic.myaccount.settings.iHaveACode")}
          </Button>
        )}
      </form>
    </Dialog>
  );
}
